import { useMutation } from "@tanstack/react-query";
import { useState, useCallback, useRef } from "react";

import type { ChatMessage, ChatMode, QueryRequest, QueryResponse } from "../types/chat";

interface UseChatOptions {
  mode?: ChatMode;
}

interface RateLimitResponse {
  error: string;
  isRateLimited: boolean;
  retryAfterSeconds: number;
}

/**
 * Hook for managing chat state and API interactions
 * @param options - Configuration options including chat mode ('qa' or 'drafting')
 */
export function useChat(options: UseChatOptions = {}) {
  const { mode = "qa" } = options;
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [rateLimitCountdown, setRateLimitCountdown] = useState<number | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Clear any existing countdown
  const clearCountdown = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setRateLimitCountdown(null);
  }, []);

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: async (request: QueryRequest): Promise<QueryResponse> => {
      const response = await fetch("/api/query", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 401) {
          window.location.reload();
          throw new Error("Not authenticated");
        }

        const data = await response.json();

        // Handle rate limiting specially
        if (response.status === 429 && data.isRateLimited) {
          const rateLimitData = data as RateLimitResponse;
          throw Object.assign(new Error(data.error), {
            isRateLimited: true,
            retryAfterSeconds: rateLimitData.retryAfterSeconds,
            originalRequest: request,
          });
        }

        throw new Error(data.error ?? "Er ging iets mis");
      }

      return response.json();
    },
    onSuccess: (data) => {
      clearCountdown();
      // Add assistant response (user message was already added immediately)
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data.answer,
          sources: data.sources,
          timestamp: new Date(),
        },
      ]);
      setError(null);
    },
    onError: (
      err: Error & {
        isRateLimited?: boolean;
        retryAfterSeconds?: number;
        originalRequest?: QueryRequest;
      },
    ) => {
      // Handle rate limiting with automatic retry
      if (err.isRateLimited && err.retryAfterSeconds && err.originalRequest) {
        const retrySeconds = err.retryAfterSeconds + 1; // Add 1 second buffer

        // Start countdown
        setRateLimitCountdown(retrySeconds);
        setError(null);

        countdownIntervalRef.current = setInterval(() => {
          setRateLimitCountdown((prev) => {
            if (prev === null || prev <= 1) {
              clearCountdown();
              // Retry the request
              sendMessageMutation.mutate(err.originalRequest!);
              return null;
            }
            return prev - 1;
          });
        }, 1000);

        return;
      }

      // Keep the user message visible so they can see what they asked
      // Just set the error to show what went wrong
      setError(err.message || "Er ging iets mis bij het verwerken van je vraag.");
    },
  });

  // Send a message
  // @param question - The message to send to the AI
  // @param displayContent - Optional shorter content to show in the UI instead of the full question
  const sendMessage = useCallback(
    (question: string, displayContent?: string) => {
      setError(null);
      clearCountdown();

      // Add user message immediately for instant feedback
      const userMessage: ChatMessage = {
        role: "user",
        content: question,
        displayContent, // Show this in UI if provided
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);

      // Build conversation history (last 10 messages for context, excluding the just-added message)
      const conversationHistory = messages.slice(-10).map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));

      sendMessageMutation.mutate({
        question,
        conversationHistory,
        mode,
      });
    },
    [messages, sendMessageMutation, mode, clearCountdown],
  );

  // Clear conversation
  const clearConversation = useCallback(() => {
    setMessages([]);
    setError(null);
    clearCountdown();
  }, [clearCountdown]);

  return {
    messages,
    isLoading: sendMessageMutation.isPending,
    error,
    rateLimitCountdown,
    sendMessage,
    clearConversation,
  };
}
